// src/js/utils/chartTheme.ts
// Phase 181: Highcharts + DataTables Theme Adapter
//
// CHRT-03 GUARDRAIL — Non-negotiable constraint carried from src/index.ts line 73:
// getChartTheme() MUST NOT return keys: series, tooltip.formatter, chart.type, plotOptions.
// Those keys trigger Highcharts propsRequireUpdateSeries and wipe the _nullAwarePatched +
// _crosshairSyncPatched pointer sentinels set by emptyAwareTooltip.ts and crosshair-sync.ts.
// Chrome keys only: chart.backgroundColor, title/subtitle/legend text, axis chrome, tooltip surface.
//
// getChartTheme() is the ONLY sanctioned JS reader of data-bs-theme (STATE.md v1.17 standing
// constraint). No other module may branch on data-bs-theme.

import * as Highcharts from 'highcharts';

// ---------------------------------------------------------------------------
// Private helper: read a computed CSS custom property from the cascade.
// MUST use getComputedStyle (not style.getPropertyValue) — the inline-style
// accessor reads only inline overrides, missing the [data-bs-theme] cascade
// (RESEARCH.md §Pitfall 2).
// ---------------------------------------------------------------------------
function token(name: string, fallback = ''): string {
    // WR-01: an unresolved custom property yields '' — an invalid Highcharts color.
    // Fall back to a supplied default so the fragment never carries backgroundColor:''
    // or color:''. Tokens always resolve in prod; this only guards a CSS-load failure.
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
}

// ---------------------------------------------------------------------------
// getChartTheme() — chrome adapter
//
// Returns a Highcharts.Options fragment mapping the five --ksat-* tokens to
// every Highcharts chrome surface. Call at chart construction time (reads
// live cascade state), NOT at module load.
//
// CHRT-03: no series, tooltip.formatter, chart.type, or plotOptions below.
// ---------------------------------------------------------------------------
export function getChartTheme(): Highcharts.Options {
    // WR-01 fallbacks = the dark-theme token values, so a resolve failure degrades to
    // the pre-181 hardcoded dark chrome rather than an invalid empty color.
    const surfaceBody   = token('--ksat-surface-body', 'rgb(31, 31, 31)');
    const surfaceCard   = token('--ksat-surface-card', 'rgb(43, 43, 43)');
    const textPrimary   = token('--ksat-text-primary', '#dee2e6');
    const textMuted     = token('--ksat-text-muted',   '#adb5bd');
    const border        = token('--ksat-border',       'rgb(46, 46, 46)');

    // Axis chrome — labels use textPrimary (readable on dark; --ksat-text-muted was too faint
    // to read on the dark chart surface). Lines / ticks / gridlines stay subtle. Phase 183 tuning.
    const xAxisChrome: Highcharts.XAxisOptions = {
        lineColor:     textMuted,
        tickColor:     textMuted,
        gridLineColor: border,
        labels: { style: { color: textPrimary } },
        title:  { style: { color: textPrimary } },
    };
    const yAxisChrome: Highcharts.YAxisOptions = {
        gridLineColor: border,
        labels: { style: { color: textPrimary } },
        title:  { style: { color: textPrimary } },
    };

    // Phase 183 fix: multi-axis charts pass yAxis/xAxis as ARRAYS, which REPLACE the single
    // object below during Highcharts.merge() — so the per-chart theme never reaches their
    // secondary/opposite axes (they fell back to Highcharts' #333 labels, unreadable on dark).
    // Register the axis chrome as Highcharts GLOBAL DEFAULTS too: Highcharts applies defaults to
    // EVERY axis (array elements included) at construction. Re-run here so it tracks the live
    // theme (dark/light). Only touches axis chrome — CHRT-03-safe (no series/plotOptions/type).
    Highcharts.setOptions({ xAxis: xAxisChrome, yAxis: yAxisChrome });

    return {
        chart: {
            backgroundColor: surfaceBody,
        },
        title: {
            style: { color: textPrimary },
        },
        subtitle: {
            style: { color: textPrimary },
        },
        legend: {
            itemStyle: { color: textPrimary },
        },
        xAxis: xAxisChrome,
        yAxis: yAxisChrome,
        // CHRT-03: tooltip block contains ONLY surface/style keys — no formatter key.
        // tooltip.formatter would trigger propsRequireUpdateSeries and wipe the
        // emptyAwareTooltip pointer patch (_nullAwarePatched sentinel, emptyAwareTooltip.ts:65).
        tooltip: {
            backgroundColor: surfaceCard,
            borderColor:     border,
            style: { color: textPrimary },
        },
        // CHRT-03 hold — end of getChartTheme(). No series, chart.type, or plotOptions.
    };
}

// ---------------------------------------------------------------------------
// reThemeAllCharts() — live re-theme loop
//
// Called by shared.ts themeToggle() after html.setAttribute('data-bs-theme', next)
// and by colorisInit() on accent change (chrome re-read — accent is not in the
// chrome fragment but getChartTheme() is cheap and keeps chrome consistent).
//
// Computes the fragment once, then iterates the Highcharts.charts global array.
// CHRT-03 safe: the fragment from getChartTheme() contains no propsRequireUpdateSeries
// keys, so pointer._nullAwarePatched + pointer._crosshairSyncPatched survive.
// chart.update(fragment, true, false): redraw=true, oneToOne=false, no animation.
// Instant re-theme — animated theme transitions are out of scope (v1.17 deferred).
// ---------------------------------------------------------------------------
export function reThemeAllCharts(): void {
    const fragment = getChartTheme();
    (Highcharts.charts as (Highcharts.Chart | undefined)[]).forEach(chart => {
        if (!chart) return;
        // CR-01 (D-07): preserve a chart's opted-in transparent background. The fragment
        // carries chart.backgroundColor: --ksat-surface-body; applied unconditionally it
        // overwrites backgroundColor:'transparent' on the first toggle/accent-change,
        // turning elevated charts opaque. Record the intent ONCE — before update() rewrites
        // userOptions — then omit chart.backgroundColor for those charts on every re-theme.
        const marked = chart as Highcharts.Chart & { _ksatWantsTransparentBg?: boolean };
        if (marked._ksatWantsTransparentBg === undefined) {
            marked._ksatWantsTransparentBg =
                chart.userOptions?.chart?.backgroundColor === 'transparent';
        }
        if (marked._ksatWantsTransparentBg) {
            const { chart: chartChrome, ...restFragment } = fragment;
            const chartChromeNoBg = { ...(chartChrome ?? {}) };
            delete (chartChromeNoBg as { backgroundColor?: unknown }).backgroundColor;
            chart.update({ ...restFragment, chart: chartChromeNoBg }, false, false);
        } else {
            chart.update(fragment, false, false);
        }
        // Phase 183: chart.update()'s single xAxis/yAxis fragment only reaches axis[0]. For
        // multi-axis charts (secondary/opposite axes), update every axis individually so their
        // labels re-theme on toggle too — otherwise they keep their construction-time color.
        // Axis .update() is CHRT-03-safe (no series/plotOptions/chart.type touched).
        (chart.xAxis || []).forEach(ax => ax.update(fragment.xAxis as Highcharts.XAxisOptions, false));
        (chart.yAxis || []).forEach(ax => ax.update(fragment.yAxis as Highcharts.YAxisOptions, false));
        chart.redraw(false);
    });
}

// ---------------------------------------------------------------------------
// updateAccentTaggedCharts(hex) — D-02 targeted accent series recolor
//
// Called by shared.ts colorisInit() coloris:pick + setProperty('--ksat-accent')
// save path to live-recolor only the three accent-tagged Highcharts elements:
//
//   - trakt-genre-chart  → series[0].update({ color: hex })
//   - lastfm-trend-chart → series[0].update({ color: hex })
//   - lastfm-heatmap     → chart.update({ colorAxis: { maxColor: hex } })
//
// Phase 181-03 CONFIRMED: real DOM container ids from trakt.ts + lastfm.ts:
//   - 'trakt-genre-chart'      (trakt genre bar chart, series[0].color = accent)
//   - 'lastfm-trend-chart'     (last.fm scrobble trend area, series[0].color = accent)
//   - 'lastfm-heatmap-matrix'  (last.fm hour×weekday heatmap, colorAxis.maxColor = accent)
//
// CHRT-03: series[0].update({ color }) does NOT touch propsRequireUpdateSeries
// (the pointer reset guard). colorAxis is also safe — not in the series-wipe list.
// If a container id is absent (chart not yet on page), the helper no-ops safely.
// ---------------------------------------------------------------------------
export function updateAccentTaggedCharts(hex: string): void {
    const accentSeriesIds  = ['trakt-genre-chart', 'lastfm-trend-chart'];
    const accentHeatmapIds = ['lastfm-heatmap-matrix'];

    (Highcharts.charts as (Highcharts.Chart | undefined)[]).forEach(chart => {
        if (!chart) return;
        const containerId: string | undefined = ((chart as any).renderTo as HTMLElement | undefined)?.id;
        if (!containerId) return;

        if (accentSeriesIds.includes(containerId)) {
            // Series line/bar color tracks --ksat-accent (D-02)
            if (chart.series && chart.series[0]) {
                chart.series[0].update({ color: hex } as Highcharts.SeriesOptionsType, true);
            }
        } else if (accentHeatmapIds.includes(containerId)) {
            // colorAxis.maxColor tracks --ksat-accent (D-02).
            // colorAxis is NOT in propsRequireUpdateSeries — CHRT-03 safe.
            chart.update({ colorAxis: { maxColor: hex } } as Highcharts.Options, true);
        }
    });
}
